import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useEffect, useState } from "react";
import z from "zod";

import { CreateTeamModal } from "@/components/teams/create-team-modal";
import { DeleteTeamDialog } from "@/components/teams/delete-team-modal";
import { EditTeamModal } from "@/components/teams/edit-team-modal";
import { TeamDetailsCard } from "@/components/teams/team-details-card";
import { TeamList } from "@/components/teams/team-list";
import { TeamsSearchHeader } from "@/components/teams/teams-search-header";
import { PAGINATION } from "@/constants/pagination";
import { useTeamsPaginated } from "@/hooks/queries/team/use-team-queries";
import { useTeamManagement } from "@/hooks/use-team-management";
import { createPaginationProps } from "@/types/pagination.types";
import { Team } from "@/types/team.types";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

const ManagementTeamsQueryParamsSchema = z.object({
    teamId: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.MANAGEMENT_TEAMS)({
    component: ManagementTeams,
    staticData: {
        config: {
            breadcrumb: {
                title: "teams",
                to: APP_ROUTES_TO.MANAGEMENT_TEAMS,
            },
            title: "teams",
        },
    },
    validateSearch: zodValidator(ManagementTeamsQueryParamsSchema),
});

function ManagementTeams() {
    const searchParams = Route.useSearch();
    const navigate = Route.useNavigate();

    const [currentTeamsPage, setCurrentTeamsPage] = useState(1);
    const [teamSearchQuery, setTeamSearchQuery] = useState("");

    const { data: teamsResponse, isLoading: isLoadingTeams } = useTeamsPaginated({
        page: currentTeamsPage,
        search: teamSearchQuery,
    });

    const selectedTeamId = searchParams.teamId;

    const {
        selectedTeam,
        isEditTeamDialogOpen,
        editTeamName,
        editTeamColor,
        editTeamSortOrder,
        showEditTeamColorPicker,
        tempTeamMembers,
        isCreatingTeam,
        newTeamName,
        newTeamColor,
        showColorPicker,
        showDeleteDialog,
        teamToDelete,
        userSearchQuery,
        isUpdatingMember,
        teamNameError,
        isValidatingName,
        currentPage,
        isShaking,
        unassignedUsersData,
        isLoadingUsers,
        createTeamMutation,
        updateTeamMutation,
        deleteTeamMutation,
        handlePageChange,
        removeTeam,
        handleDeleteClick,
        onTeamClickHandler,
        openEditTeamDialog,
        closeEditTeamDialog,
        saveTeamChanges,
        onTeamMemberRoleChange,
        handleCreateTeam,
        handleCreateTeamKeyDown,
        resetCreateTeamForm,
        setUserSearchQuery,
        setIsCreatingTeam,
        setNewTeamName,
        setNewTeamColor,
        setShowColorPicker,
        setEditTeamName,
        setEditTeamColor,
        setEditTeamSortOrder,
        setShowEditTeamColorPicker,
        setShowDeleteDialog,
        setSelectedTeam,
    } = useTeamManagement({ teamsList: teamsResponse.data });

    useEffect(() => {
        if (selectedTeamId && teamsResponse.data.length > 0) {
            const found = teamsResponse.data.find((t) => t.id === selectedTeamId);
            if (found && (!selectedTeam || selectedTeam.id !== found.id)) {
                setSelectedTeam(found);
            }
        }
    }, [selectedTeamId, teamsResponse.data]);

    const handleTeamClick = (team: Team) => {
        navigate({ to: Route.to, search: { teamId: team.id } });
        onTeamClickHandler(team);
    };

    const handleTeamsPageChange = (page: number) => {
        setCurrentTeamsPage(page);
    };

    const handleTeamSearchChange = (query: string) => {
        setTeamSearchQuery(query);
        setCurrentTeamsPage(PAGINATION.DEFAULT_PAGE);
    };

    const isLoading = isLoadingTeams || isLoadingUsers;

    return (
        <>
            <TeamsSearchHeader
                teamSearchQuery={teamSearchQuery}
                onTeamSearchChange={handleTeamSearchChange}
                onCreateTeamClick={() => setIsCreatingTeam(true)}
                isCreatingTeamLoading={createTeamMutation.isPending}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0 h-0">
                <div className="h-full min-h-0 flex flex-col lg:col-span-1">
                    <TeamList
                        teams={teamsResponse.data}
                        selectedTeam={selectedTeam}
                        onTeamClick={handleTeamClick}
                        isLoading={isLoading}
                        isShaking={isShaking}
                        pagination={createPaginationProps({
                            response: teamsResponse,
                            onPageChange: handleTeamsPageChange,
                            isLoading,
                        })}
                    />
                </div>
                <div className="h-full min-h-0 flex flex-col lg:col-span-2">
                    <TeamDetailsCard
                        selectedTeam={selectedTeam}
                        tempTeamMembers={tempTeamMembers}
                        userSearchQuery={userSearchQuery}
                        currentPage={currentPage}
                        pageSize={PAGINATION.PAGE_SIZE}
                        isUpdatingMember={isUpdatingMember}
                        updateTeamMutation={updateTeamMutation}
                        deleteTeamMutation={deleteTeamMutation}
                        onOpenEditDialog={openEditTeamDialog}
                        onHandleDeleteClick={handleDeleteClick}
                        onUserSearchChange={setUserSearchQuery}
                        onTeamMemberRoleChange={onTeamMemberRoleChange}
                        onPageChange={handlePageChange}
                        unassignedUsersData={unassignedUsersData || { users: [], total: 0 }}
                    />
                </div>
            </div>

            <CreateTeamModal
                isOpen={isCreatingTeam}
                onClose={resetCreateTeamForm}
                newTeamName={newTeamName}
                setNewTeamName={setNewTeamName}
                newTeamColor={newTeamColor}
                setNewTeamColor={setNewTeamColor}
                showColorPicker={showColorPicker}
                setShowColorPicker={setShowColorPicker}
                isShaking={isShaking}
                onCreateTeam={handleCreateTeam}
                onCreateTeamKeyDown={handleCreateTeamKeyDown}
                isCreatingTeamLoading={createTeamMutation.isPending}
            />

            <EditTeamModal
                isOpen={isEditTeamDialogOpen}
                onClose={closeEditTeamDialog}
                teamName={editTeamName}
                onTeamNameChange={setEditTeamName}
                teamColor={editTeamColor}
                onTeamColorChange={setEditTeamColor}
                teamSortOrder={editTeamSortOrder}
                onTeamSortOrderChange={setEditTeamSortOrder}
                showColorPicker={showEditTeamColorPicker}
                setShowColorPicker={setShowEditTeamColorPicker}
                teamNameError={teamNameError}
                isValidatingName={isValidatingName}
                isShaking={isShaking}
                onSave={saveTeamChanges}
                isSaving={updateTeamMutation.isPending}
            />

            <DeleteTeamDialog
                isOpen={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
                teamName={selectedTeam?.name}
                onDelete={() => teamToDelete && removeTeam(teamToDelete)}
                isDeletingTeam={deleteTeamMutation.isPending}
            />
        </>
    );
}
